/**
 * DemoSimulation — the heartbeat of the simulated build.
 *
 * On a fixed interval it drives the *currently selected* topology by emitting
 * real-shape frames over the MockWsClient: EventBus event frames
 * (`routing.delivered`, `agent.status-changed`, `run.failed`) that the
 * pixel-world bridge animates, and MFP feedback frames (progress / completion /
 * error) that the activity ticker shows. In parallel it accrues cost/tokens and
 * cycles run lifecycles in the demo backend, so the HUD stats and runs dock
 * evolve. Nothing here knows about React — it just pushes frames and mutates
 * the model the REST mock reads from.
 */

import { useWorldStore } from "../stores/world.js";
import { demoBackend } from "./demoBackend.js";
import { DEFAULT_DEMO_TOPOLOGY_ID, demoTopologyDefinition } from "./topologies.js";
import type { MockWsClient } from "./MockWsClient.js";

const PROGRESS_PHRASES = [
  "Reading shared context…",
  "Calling tool: web_search",
  "Delegating sub-task",
  "Synthesizing findings",
  "Drafting response",
  "Validating output",
  "Checking security policy",
  "Querying memory store",
  "Waiting on peer result",
  "Scoring candidate answers",
];

const ERROR_PHRASES = [
  "Tool call failed — retrying",
  "Rate limited, backing off",
  "Downstream agent timed out",
  "Validation failed, re-planning",
];

const TICK_MS = 850;

function uid(prefix: string): string {
  const rnd =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${prefix}-${rnd}`;
}

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

interface RunStep {
  count: number;
  length: number;
}

export class DemoSimulation {
  private ws: MockWsClient | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private steps = new Map<string, RunStep>();

  start(ws: MockWsClient): void {
    if (this.intervalId != null) return;
    this.ws = ws;
    this.tick();
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
  }

  stop(): void {
    if (this.intervalId != null) clearInterval(this.intervalId);
    this.intervalId = null;
    this.ws = null;
  }

  private emit(frame: unknown): void {
    this.ws?.emit(frame);
  }

  private eventFrame(category: string, fields: Record<string, unknown>): unknown {
    return {
      type: "feedback",
      message: { _type: "event", eventId: uid("evt"), timestamp: Date.now(), category, ...fields },
    };
  }

  private feedbackFrame(
    kind: "progress" | "completion" | "error",
    agent: { id: string; name?: string },
    message: string,
    runId: string,
  ): unknown {
    return {
      type: "feedback",
      message: {
        id: uid("fb"),
        type: kind,
        source: { agentId: agent.id, name: agent.name ?? agent.id },
        payload: { message },
        timestamp: new Date().toISOString(),
        _meta: { runId },
      },
    };
  }

  private tick(): void {
    const topologyId = useWorldStore.getState().selectedTopologyId ?? DEFAULT_DEMO_TOPOLOGY_ID;
    const def = demoTopologyDefinition(topologyId);
    const agents = def.agents;
    if (agents.length === 0) return;

    const run = demoBackend.ensureActiveRun(topologyId);
    const step = this.steps.get(topologyId) ?? { count: 0, length: randInt(10, 18) };

    // 1. Route a message along a real edge → envelope animation + light up the receiver.
    if (def.edges.length > 0) {
      const edge = pick(def.edges);
      this.emit(
        this.eventFrame("routing.delivered", {
          topologyId,
          runId: run.id,
          message: { id: uid("msg"), from: edge.from, to: edge.to },
        }),
      );
      this.emit(
        this.eventFrame("agent.status-changed", {
          topologyId,
          runId: run.id,
          agentId: edge.to,
          status: "running",
          previousStatus: "idle",
        }),
      );
    }

    // 2. A progress note from a random agent + vary its visible status.
    const agent = pick(agents);
    this.emit(this.feedbackFrame("progress", agent, pick(PROGRESS_PHRASES), run.id));
    this.emit(
      this.eventFrame("agent.status-changed", {
        topologyId,
        runId: run.id,
        agentId: agent.id,
        status: pick(["running", "running", "waiting", "idle"]),
      }),
    );

    // 3. Accrue cost/tokens onto the active run + global totals.
    demoBackend.bumpRun(run, {
      cost: 0.0008 + Math.random() * 0.004,
      tokensIn: 120 + randInt(0, 600),
      tokensOut: 60 + randInt(0, 300),
    });

    // 4. Lifecycle: occasionally fail, otherwise complete; then a fresh run begins.
    step.count++;
    if (step.count >= step.length) {
      const failed = Math.random() < 0.12;
      const entry = agents.find((a) => a.id === def.entryPoint) ?? agents[0];
      if (failed) {
        const culprit = pick(agents);
        this.emit(this.feedbackFrame("error", culprit, pick(ERROR_PHRASES), run.id));
        this.emit(
          this.eventFrame("run.failed", {
            topologyId,
            runId: run.id,
            agentId: culprit.id,
            error: "Simulated failure",
          }),
        );
      } else {
        this.emit(this.feedbackFrame("completion", entry, `Completed: ${run.input}`, run.id));
      }
      demoBackend.completeRun(run, failed);
      for (const a of agents) {
        this.emit(
          this.eventFrame("agent.status-changed", {
            topologyId,
            runId: run.id,
            agentId: a.id,
            status: "idle",
          }),
        );
      }
      this.steps.set(topologyId, { count: 0, length: randInt(10, 18) });
      return;
    }

    this.steps.set(topologyId, step);
  }
}

export const demoSimulation = new DemoSimulation();
